import React, { useState } from 'react';
import { crearProfesionalApi, guardarDatosProfesionalApi, renombrarProfesionalAgendaApi } from '../services/api';

// Datos de un profesional: se usa en la solapa Profesionales y al tocar el
// nombre de un profesional en Agendas. Sin profesional, es un alta.
export const TIPOS_PROFESIONAL = [
  { key: 'rehabilitacion', label: 'Rehabilitación' },
  { key: 'integracion', label: 'Integración' },
];
export const etiquetaTipo = (tipo) => TIPOS_PROFESIONAL.find((t) => t.key === tipo)?.label || '';

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const etiqueta = 'block text-xs font-bold text-slate-600 dark:text-slate-300';
const botonSecundario =
  'inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const botonPrimario = 'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50';

export default function FormularioProfesional({ profesional = null, alGuardado, alCancelar }) {
  const nuevo = !profesional;
  const [datos, setDatos] = useState({
    nombres: profesional?.nombres || '',
    apellido: profesional?.apellido || '',
    dni: profesional?.dni || '',
    email: profesional?.email || '',
    tipo: profesional?.tipo || '',
    nombreAgenda: profesional?.nombre || '',
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const cambiar = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value }));
  const sugerido = `${datos.nombres} ${datos.apellido}`.replace(/\s+/g, ' ').trim();

  const guardar = async (e) => {
    e.preventDefault();
    if (guardando) return;
    if (!datos.tipo) { setError('Elegí si es de rehabilitación o de integración.'); return; }
    setGuardando(true); setError('');
    const { nombreAgenda, ...resto } = datos;
    try {
      let guardado;
      if (nuevo) {
        guardado = await crearProfesionalApi({ ...resto, nombreAgenda: nombreAgenda.trim() });
      } else {
        // El nombre en la agenda se cambia primero: también lo actualiza en la agenda y en los emails.
        if (nombreAgenda.trim() && nombreAgenda.trim() !== profesional.nombre) await renombrarProfesionalAgendaApi(profesional.id, nombreAgenda.trim());
        guardado = await guardarDatosProfesionalApi(profesional.id, resto);
      }
      await alGuardado?.(guardado);
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los datos.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} className="space-y-3" aria-label={nuevo ? 'Nuevo profesional' : `Datos de ${profesional.nombre}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={etiqueta}>
          <span className="mb-1 block">Nombre</span>
          <input className={inputClass} value={datos.nombres} onChange={cambiar('nombres')} maxLength={80} required autoComplete="off" disabled={guardando} />
        </label>
        <label className={etiqueta}>
          <span className="mb-1 block">Apellido</span>
          <input className={inputClass} value={datos.apellido} onChange={cambiar('apellido')} maxLength={80} required autoComplete="off" disabled={guardando} />
        </label>
        <label className={etiqueta}>
          <span className="mb-1 block">DNI</span>
          <input className={inputClass} value={datos.dni} onChange={cambiar('dni')} inputMode="numeric" maxLength={12} placeholder="Solo números" autoComplete="off" disabled={guardando} />
        </label>
        <label className={etiqueta}>
          <span className="mb-1 block">Email</span>
          {/* Texto y sin autocompletar: el navegador llenaba con el email del usuario. */}
          <input type="text" inputMode="email" spellCheck={false} data-lpignore="true" className={inputClass} value={datos.email}
            onChange={cambiar('email')} placeholder="email@ejemplo.com" autoComplete="off" disabled={guardando} />
        </label>
      </div>
      <fieldset>
        <legend className={`${etiqueta} mb-1`}>Tipo</legend>
        <div className="flex flex-wrap gap-2">
          {TIPOS_PROFESIONAL.map((t) => (
            <label key={t.key} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
              datos.tipo === t.key ? 'border-emerald-500 bg-emerald-50 font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'border-slate-200 text-slate-700 dark:border-slate-600 dark:text-slate-200'
            }`}>
              <input type="radio" name="tipo-profesional" value={t.key} checked={datos.tipo === t.key} onChange={cambiar('tipo')} disabled={guardando} />
              {t.label}
            </label>
          ))}
        </div>
      </fieldset>
      <label className={etiqueta}>
        <span className="mb-1 block">Nombre en la agenda</span>
        <input className={inputClass} value={datos.nombreAgenda} onChange={cambiar('nombreAgenda')} maxLength={120} autoComplete="off"
          placeholder={nuevo ? sugerido || 'Nombre y apellido' : ''} required={!nuevo} disabled={guardando} />
        <span className="mt-1 block font-normal text-slate-500">
          {nuevo ? 'Si lo dejás vacío se usa el nombre y el apellido.' : 'Si lo cambiás, también se cambia en la agenda.'}
        </span>
      </label>
      {error && <p role="alert" className="text-sm font-semibold text-rose-700">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        {alCancelar && <button type="button" className={botonSecundario} onClick={alCancelar} disabled={guardando}>Cancelar</button>}
        <button type="submit" className={botonPrimario} disabled={guardando}>{guardando ? 'Guardando…' : nuevo ? 'Agregar profesional' : 'Guardar datos'}</button>
      </div>
    </form>
  );
}
